import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setActiveLugs } from 'app/slices/ahuBoardSlice';
import { ahuBoardApi, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import {
  useGetLugParamsQuery,
  useCreateTunnelLugMutation,
  useCreateLugSettingMutation,
  usePatchLugSettingMutation,
  useDeleteLugSettingMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardTunnel, IAhuTunnelLugPlacement, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { cmsBaseList } from '../../forms/cmsDoc';
import { catalogLabel } from '../../forms/formUtils';
import { UniversalPopup, DeleteConfirmPopup } from '../SharedPopups';

/**
 * Phase 6e1 — Base & Lugs / lifting-lug pane (§7.3.2, tab.lifting.lug.js +
 * tab.lifting.lug.tmpl, markup faithful). THE lug-selection receiving end:
 * consumes 6d's `lugs.activeLugIds` slice seam + the canvas
 * `change:active:lugs` broadcast (tab switch / accordion-open halves are the
 * canvas's slice writes; this pane maps lug ids → position records, retargets
 * the tunnel pill, renders single or merged "varies..." settings).
 *
 * Lug-defaults check/uncheck warnings use the EXACT legacy copy with
 * revert-then-confirm semantics (config block ported verbatim; parity 183).
 */

const LUG_DEFAULTS_COPY = {
  uncheck: {
    title: 'Warning!',
    content:
      "If you uncheck the Defaults, you will be granted the ability to customize the placement and features of your lugs, but you will take the risk of violating the manufacturer's lug application rules.",
    actionText: 'UNCHECK',
    newValue: false,
  },
  check: {
    title: 'Warning!',
    content:
      "If you check the Defaults, you will lose all customization applied to the placement and features of the lugs and reset all values to the manufacturer's standards.",
    actionText: 'CHECK',
    newValue: true,
  },
} as const;

type LugPosition = Record<string, unknown> & { id?: number | string };

interface LiftingLugPaneProps {
  tunnels: IAhuBoardTunnel[];
  cms: ICmsAhuDoc | undefined;
  open: boolean;
}

const MERGE_KEYS = [
  'attachment_name',
  'base_direction',
  'x',
  'y',
  'model',
  'max_weight_per_lug',
  'max_center_spacing',
  'min_dist_from_base_edge',
] as const;

/** mergeObjects — differing values → null ("varies..."). */
function mergeLugs(lugs: LugPosition[]): LugPosition {
  const merged: LugPosition = { ...lugs[0] };
  MERGE_KEYS.forEach((key) => {
    const first = lugs[0]?.[key];
    if (!lugs.every((lug) => lug[key] === first)) merged[key] = null;
  });
  return merged;
}

const LiftingLugPane = ({ tunnels, cms, open }: LiftingLugPaneProps) => {
  const dispatch = useAppDispatch();
  const activeLugIds = useAppSelector((s) => s.ahuBoard.lugs.activeLugIds);
  const activeLugTunnelId = useAppSelector((s) => s.ahuBoard.lugs.activeTunnelId);
  const { data: lugParams } = useGetLugParamsQuery();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const [createTunnelLug] = useCreateTunnelLugMutation();
  const [createLugSetting] = useCreateLugSettingMutation();
  const [patchLugSetting] = usePatchLugSettingMutation();
  const [deleteLugSetting] = useDeleteLugSettingMutation();

  // Based tunnels only get lug models (parity 121) — that is about placements,
  // NOT about the tunnel strip: legacy renders a pill for EVERY tunnel
  // (`tunnels: tunnels.toJSON()`, tab.lifting.lug:307) and only filters when
  // building lug models. Filtering the strip too hid every tunnel that has yet
  // to be given a Base — including a freshly added one, whose Base you set
  // from this very pane.
  const basedTunnels = useMemo(() => tunnels.filter((t) => t.base_id), [tunnels]);
  const [activeTunnelId, setActiveTunnelId] = useState<number | null>(
    tunnels[0]?.id ?? null
  );
  const [placements, setPlacements] = useState<Record<number, IAhuTunnelLugPlacement>>({});
  const [pendingRecalc, setPendingRecalc] = useState<Record<number, boolean>>({});
  const [recalcRunning, setRecalcRunning] = useState(false);
  const [showCmsInfo, setShowCmsInfo] = useState(false);
  const [defaultsPopup, setDefaultsPopup] = useState<'check' | 'uncheck' | null>(null);
  const [deletePopup, setDeletePopup] = useState(false);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;

  const activeTunnel = tunnels.find((t) => t.id === activeTunnelId);

  // Pill follows the canvas cross-tunnel lug pick (§13 row 14).
  useEffect(() => {
    if (activeLugTunnelId != null && activeLugTunnelId !== activeTunnelId) {
      setActiveTunnelId(activeLugTunnelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLugTunnelId]);

  // Adding or deleting a tunnel re-renders this pane through the refreshed
  // tunnel list, so the strip follows on its own; what does not is the
  // selection, which can be left pointing at a tunnel that is gone. Legacy
  // re-homed it to the first tunnel (updateTunnels:256-262) — same here, and it
  // also seeds the selection when the board opens with no tunnels at all.
  useEffect(() => {
    if (!tunnels.length) return;
    if (tunnels.some((t) => t.id === activeTunnelId)) return;
    setActiveTunnelId(tunnels[0].id);
    dispatch(setActiveLugs({ ids: [], tunnelId: tunnels[0].id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tunnels, activeTunnelId]);

  const fetchPlacement = (tunnelId: number, force = true) =>
    dispatch(
      ahuBoardApi.endpoints.getTunnelLugPlacement.initiate(tunnelId, {
        forceRefetch: force,
      })
    )
      .unwrap()
      .then((placement) => {
        setPlacements((prev) => ({ ...prev, [tunnelId]: placement }));
        return placement;
      })
      .catch(() => undefined);

  // A placement only exists for a tunnel that is based AND carries lugs. Legacy
  // holds a LugCollection model for based tunnels only and routes every refetch
  // through `refetchModels`, which returns early with no model
  // (collection/tunnel.lugs.js:33-35); the lug half is ours — with the Base a
  // tunnel has nothing else the response feeds, so there is nothing to GET.
  const tunnelHasLugs = (tunnelId: number) =>
    tunnels.some((t) => t.id === tunnelId && t.base_id != null && t.lug);

  const refetchPlacement = (tunnelId: number) =>
    tunnelHasLugs(tunnelId) ? fetchPlacement(tunnelId) : Promise.resolve(undefined);

  // `model.hide_lugs = true` (tab.lifting.lug:513). The canvas reads the query
  // cache, so writing the flag there is what legacy's single shared collection
  // gave for free: `redrawTunnelLugs` clears the tunnel's lugs and then bails on
  // the flag instead of drawing the stale ones back. The next successful
  // refetch replaces the entry, flag and all, which is legacy's `hide_lugs =
  // false` on refetchModels (collection/tunnel.lugs.js:36).
  const markPlacementStale = (tunnelId: number) =>
    dispatch(
      ahuBoardApi.util.updateQueryData('getTunnelLugPlacement', tunnelId, (draft) => {
        draft.hide_lugs = true;
      })
    );

  // Create-or-refetch, on the same fork legacy takes after a tunnel save
  // (tunnel.js:311-317): no model held for this tunnel → `createModel`, which
  // POSTs and keeps the response; a model already held → `refetchModels`, which
  // GETs. So turning lugs on for the first time creates the record rather than
  // GETting one that isn't there — React never ported either of legacy's two
  // creation points (that POST, and the auto-create ridden in on
  // GET ahu/tunnels/<id>/, tunnel_viewset.py:130), which is why these tunnels
  // have no AHULug row at all. POST is idempotent server-side
  // (AHULugSerializer.create reuses an existing row), so a stale local miss
  // costs a redundant write, never a duplicate.
  const ensurePlacement = (tunnelId: number) =>
    placementsRef.current[tunnelId]
      ? fetchPlacement(tunnelId)
      : createTunnelLug({ tunnel_id: tunnelId })
          .unwrap()
          .then((created) => {
            setPlacements((prev) => ({ ...prev, [tunnelId]: created }));
            return created;
          })
          .catch(() => undefined);

  // Initial placements (one per lug-bearing tunnel), deferred to the first open:
  // the pane stays mounted while Base & Lugs is collapsed, and nothing it renders
  // in that state reads a placement. Unforced, so it reuses whatever the board
  // bootstrap already cached rather than re-requesting it.
  const lugTunnelIds = basedTunnels.filter((t) => t.lug).map((t) => t.id);
  useEffect(() => {
    if (!open) return;
    lugTunnelIds.forEach((id) => {
      if (!placementsRef.current[id]) void fetchPlacement(id, false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lugTunnelIds.join(',')]);

  // `tunnelLugs:recalcPlacement` / `recalc:lug:placement` / `render_tunnel_lugs` (§7.3.2).
  useEffect(() => {
    const offs = [
      ahuBoardBus.on('tunnelLugs:recalcPlacement', ({ tunnel_id }) => {
        // addTunnelIdForRecalc:509 — `if (!this.models[tunnel_id]) return`. The
        // emitters (section/accessory/dmount forms) fire for any tunnel, so one
        // with no lugs would otherwise arm ReCalculate and GET on the way out.
        if (!tunnelHasLugs(tunnel_id)) return;
        setPendingRecalc((prev) => ({ ...prev, [tunnel_id]: true }));
        // Arming the button means the drawn lugs no longer match the tunnel, so
        // they come off the canvas now. Legacy stamped `hide_lugs` on the shared
        // model here (:513) but only redrew when the caller passed
        // `hide_from_canvas`, which left stale lugs on screen for every other
        // emitter; we drop that condition and always take them down.
        markPlacementStale(tunnel_id);
        ahuBoardBus.emit('redraw:tunnel:lugs', tunnel_id, true);
      }),
      ahuBoardBus.on('recalc:lug:placement', () => {
        setRecalcRunning(true);
        const ids = Object.keys(pendingRecalc).map(Number);
        Promise.all(ids.map((id) => refetchPlacement(id)))
          .then(() => {
            setPendingRecalc({});
            setRecalcRunning(false);
            ids.forEach((id) => ahuBoardBus.emit('redraw:tunnel:lugs', id));
          })
          .catch(() => setRecalcRunning(false));
      }),
      ahuBoardBus.on('render_tunnel_lugs', () => {
        // post tunnel delete — re-sync tunnels list (props already fresh).
        setPlacements((prev) => {
          const next: Record<number, IAhuTunnelLugPlacement> = {};
          basedTunnels.forEach((t) => {
            if (prev[t.id]) next[t.id] = prev[t.id];
          });
          return next;
        });
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRecalc, basedTunnels]);

  // ReCalculate button arming (§7.3.2 / parity 126).
  const recalcArmed = Object.keys(pendingRecalc).length > 0;
  const recalcButton = (
    <button
      id="recalculate_lug_placement"
      className={`btn${recalcArmed ? ' warning' : ' non-clickable'}`}
      onClick={() => {
        if (recalcArmed) ahuBoardBus.emit('recalc:lug:placement');
      }}
    >
      <div className={`preloader dotted-preloader${recalcRunning ? ' active' : ''}`} />
      ReCalculate tunnel Lug Placement
    </button>
  );

  if (!open) {
    return (
      <>
        <ul id="lifting-lug-wrapper-tab" className="sublist">
          <li className="spinner-wrapper small">
            <div className="preloader dotted-preloader active" />
          </li>
        </ul>
        {recalcButton}
      </>
    );
  }

  const placement = activeTunnelId != null ? placements[activeTunnelId] : undefined;
  const positions = (placement?.positions ?? []) as LugPosition[];
  const lugModels = ((placement as Record<string, unknown> | undefined)?.models ??
    []) as Array<Record<string, unknown>>;
  const lugBaseDirections = ((placement as Record<string, unknown> | undefined)
    ?.lug_base_direction ?? []) as Array<Record<string, unknown>>;
  const paramsData = (lugParams ?? {}) as {
    tunnel?: { base_direction?: Array<Record<string, unknown>>; to_avoid_lug?: unknown[] };
  };
  const tunnelBaseDirections = paramsData.tunnel?.base_direction ?? [];

  const activeLugPositions = positions.filter((p) =>
    activeLugIds.some((id) => String(id) === String(p.id))
  );
  const activeLug: LugPosition | null =
    activeLugPositions.length > 1
      ? mergeLugs(activeLugPositions)
      : activeLugPositions[0] ?? null;
  const emptyOptionName = activeLugPositions.length > 1 ? 'varies...' : '- -';
  const lastTunnel = tunnels[tunnels.length - 1];
  const showHint = (() => {
    const idx = String(lastTunnel?.index ?? '').split(':').map(Number);
    return idx.length === 2 && idx[0] > idx[1];
  })();

  const defaultsOn = !!activeTunnel?.tunnel_lug_defaults;
  const lugOn = !!activeTunnel?.lug;
  // Legacy gated Include Lifting Lugs on the placement's `models` list
  // (tab.lifting.lug:324), which greys the checkbox out for any tunnel whose
  // placement 404s — locking lugs OFF on exactly the tunnels that have none
  // yet, the state you need it in to turn them on. `has_lug_models` asks the
  // same question of the tunnel rather than the placement, so it answers for a
  // tunnel with no AHULug row: false with no Base, otherwise whether any lug
  // model fits that Base. The placement's list still fills the Model select.
  const disableLug = !activeTunnel?.has_lug_models;

  // Tunnel-settings change → PATCH tunnel → refetch lugs → re-render (§7.3.2).
  const changeTunnelSetting = (name: string, value: unknown) => {
    if (activeTunnelId == null || !activeTunnel) return;
    // Clearing the Base — or unticking Include Lifting Lugs — drops the placement
    // rather than refetching it (legacy tunnel.js:311-313 → LugCollection.removeModel):
    // the tunnel has no lugs left to place, so there is nothing to GET. Decide off
    // the post-PATCH value; the `tunnels` prop is still the pre-PATCH one here.
    const keepsLugs =
      !!(name === 'base_id' ? value : activeTunnel.base_id) &&
      !!(name === 'lug' ? value : activeTunnel.lug);
    setRecalcRunning(true);
    patchTunnel({ id: activeTunnelId, ahuId: activeTunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        if (keepsLugs) return ensurePlacement(activeTunnelId);
        setPlacements((prev) => {
          const next = { ...prev };
          delete next[activeTunnelId];
          return next;
        });
        return undefined;
      })
      .then(() => {
        setRecalcRunning(false);
        ahuBoardBus.emit('redraw:tunnel:lugs', activeTunnelId, !keepsLugs);
      })
      .catch(() => setRecalcRunning(false));
  };

  // Per-lug change — PATCH each active lug setting + redraw (§7.3.2).
  const changeLugField = (name: string, value: unknown) => {
    if (activeTunnelId == null || !activeTunnel) return;
    activeLugPositions.forEach((lug) => {
      const lugId = Number(lug.id);
      if (!Number.isFinite(lugId)) return;
      patchLugSetting({ id: lugId, data: { [name]: value } })
        .unwrap()
        // Take the whole saved row, not `{[name]: value}`. One field can carry
        // others with it -- Model brings its own `attachment_name` and
        // `model_details` -- and merging only what we sent left the readouts
        // showing the previous model's values. It also matters to the canvas:
        // `drawLug` dispatches on `lug.model_details.style`, so a partial row
        // has no style to draw with.
        .then((saved) => {
          const row = { ...lug, ...(saved as LugPosition) };
          setPlacements((prev) => {
            const cur = prev[activeTunnelId];
            if (!cur) return prev;
            const nextPositions = ((cur.positions ?? []) as LugPosition[]).map((p) =>
              String(p.id) === String(lugId) ? row : p
            );
            return { ...prev, [activeTunnelId]: { ...cur, positions: nextPositions } };
          });
          ahuBoardBus.emit('redraw:tunnel:lug', activeTunnelId, row);
        })
        .catch(() => undefined);
    });
  };

  const addLug = () => {
    if (activeTunnelId == null) return;
    createLugSetting({ tunnel_id: activeTunnelId })
      .unwrap()
      .then((newLugs) => {
        const rows = (Array.isArray(newLugs) ? newLugs : [newLugs]) as LugPosition[];
        setPlacements((prev) => {
          const cur = prev[activeTunnelId];
          if (!cur) return prev;
          return {
            ...prev,
            [activeTunnelId]: {
              ...cur,
              positions: [...((cur.positions ?? []) as LugPosition[]), ...rows],
            },
          };
        });
        rows.forEach((lug) => ahuBoardBus.emit('redraw:tunnel:lug', activeTunnelId, lug));
        // Select what was just added: the canvas tints it and answers with
        // `change:active:lugs`, which is what fills the Lug Settings fields
        // below — so Add leaves you on the new lug instead of on nothing.
        // Emitted after the draws; the lug-style builders call back
        // synchronously, so the shapes are registered by now.
        ahuBoardBus.emit(
          'select:tunnel:lugs',
          rows.map((lug) => lug.id as number | string)
        );
      })
      .catch(() => undefined);
  };

  const deleteActiveLugs = () => {
    if (activeTunnelId == null) return;
    Promise.all(
      activeLugPositions.map((lug) =>
        deleteLugSetting(Number(lug.id))
          .unwrap()
          .then(() => {
            ahuBoardBus.emit('redraw:tunnel:lug', activeTunnelId, { id: lug.id }, true);
          })
          .catch(() => undefined)
      )
    ).then(() => {
      setPlacements((prev) => {
        const cur = prev[activeTunnelId];
        if (!cur) return prev;
        return {
          ...prev,
          [activeTunnelId]: {
            ...cur,
            positions: ((cur.positions ?? []) as LugPosition[]).filter(
              (p) => !activeLugPositions.some((a) => String(a.id) === String(p.id))
            ),
          },
        };
      });
      dispatch(setActiveLugs({ ids: [], tunnelId: activeTunnelId }));
      setDeletePopup(false);
    });
  };

  const confirmDefaults = (mode: 'check' | 'uncheck') => {
    const cfg = LUG_DEFAULTS_COPY[mode];
    if (activeTunnelId == null || !activeTunnel) return;
    // CHECK/UNCHECK dismisses the popup on the click. The PATCH and the refetch
    // behind it are reported by the pane spinner and the one on the ReCalculate
    // button — legacy raised both before opening the popup
    // (tab.lifting.lug:131-133) but only called popup.remove() once the save had
    // come back, leaving a dead popup sitting over its own progress.
    setDefaultsPopup(null);
    setRecalcRunning(true);
    patchTunnel({
      id: activeTunnelId,
      ahuId: activeTunnel.ahu_id,
      data: { tunnel_lug_defaults: cfg.newValue },
    })
      .unwrap()
      .then(() => {
        if (cfg.newValue) {
          return refetchPlacement(activeTunnelId).then(() => {
            dispatch(setActiveLugs({ ids: [], tunnelId: activeTunnelId }));
            setPendingRecalc((prev) => {
              const next = { ...prev };
              delete next[activeTunnelId];
              return next;
            });
            return undefined;
          });
        }
        return undefined;
      })
      .then(() => {
        ahuBoardBus.emit('redraw:tunnel:lugs', activeTunnelId, true);
        setRecalcRunning(false);
      })
      .catch(() => setRecalcRunning(false));
  };

  // Rows except .always-visible grey while defaults on (checkIsChangable).
  const lockedClass = (always: boolean) =>
    !always && defaultsOn ? ' non-clickable' : '';

  const num2 = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(1) : '';
  };

  return (
    <>
      <ul id="lifting-lug-wrapper-tab" className="sublist">
        <li className={`spinner-wrapper${recalcRunning ? '' : ' hide'}`}>
          <div className="preloader dotted-preloader active" />
        </li>
        <li className="always-visible">
          <div className="single-line">
            <div className="tunnels">
              {tunnels.map((t) => (
                <span
                  key={t.id}
                  className={`tunnel-index${t.id === activeTunnelId ? ' active' : ''}`}
                  data-id={t.id}
                  onClick={() => setActiveTunnelId(t.id)}
                >
                  {t.index}
                </span>
              ))}
            </div>
            <span className="label">
              <span> Tunnel</span>
            </span>
          </div>
        </li>

        <li className="always-visible">
          <div className="single-line">
            <select
              name="base_id"
              data-tunnel={activeTunnel?.id}
              required
              className="tunnel_settings_field"
              value={activeTunnel?.base_id ?? ''}
              onChange={(e) =>
                changeTunnelSetting('base_id', e.currentTarget.value || null)
              }
            >
              <option value="">- -</option>
              {cmsBaseList(cms).map((b) => (
                <option key={b.id} value={b.id}>
                  {catalogLabel(b)}
                </option>
              ))}
            </select>
            <span className="label">Base</span>
          </div>
        </li>

        {activeTunnel?.base_id != null && (
          <>
            <li className="always-visible">
              <div className={disableLug ? 'non-clickable' : ''}>
                <input
                  type="checkbox"
                  className="tunnel_settings_field"
                  name="lug"
                  checked={lugOn}
                  onChange={(e) => changeTunnelSetting('lug', e.currentTarget.checked)}
                />
                <span className="label">
                  <span>Include Lifting Lugs</span>
                </span>
              </div>
            </li>
            {lugOn && (
              <>
                <li className="always-visible">
                  <div className="">
                    <input
                      type="checkbox"
                      className="tunnel_settings_field"
                      name="tunnel_lug_defaults"
                      checked={defaultsOn}
                      onChange={(e) => {
                        // Revert-then-confirm semantics (§7.3.2): the popup
                        // owns the final state; checked state stays put until
                        // CONFIRM (controlled input = automatic revert).
                        setDefaultsPopup(e.currentTarget.checked ? 'check' : 'uncheck');
                      }}
                    />
                    <span className="label">
                      <span>Placement &amp; Lug Defaults</span>
                    </span>
                  </div>
                </li>
                <li className="empty-row" />
                <li className={lockedClass(false)}>
                  <div className="">
                    <span className="label bold">
                      <span>Placement Settings</span>
                    </span>
                  </div>
                </li>
                <li className={lockedClass(false)}>
                  <div className="single-line">
                    <select
                      className="tunnel_settings_field"
                      name="base_direction_id"
                      value={activeTunnel?.base_direction_id ?? ''}
                      onChange={(e) =>
                        changeTunnelSetting(
                          'base_direction_id',
                          e.currentTarget.value ? Number(e.currentTarget.value) : null
                        )
                      }
                    >
                      <option className="hide" value="">
                        - -
                      </option>
                      {tunnelBaseDirections.map((d) => (
                        <option key={Number(d.id)} value={Number(d.id)}>
                          {String(d.name ?? '')}
                        </option>
                      ))}
                    </select>
                    <span className="label">
                      <span>Base Direction</span>
                    </span>
                  </div>
                </li>
                <li className={lockedClass(false)}>
                  <div>
                    <input
                      type="checkbox"
                      className="tunnel_settings_field"
                      name="lugs_on_tunnel_adjoining_base"
                      checked={!!activeTunnel?.lugs_on_tunnel_adjoining_base}
                      onChange={(e) =>
                        changeTunnelSetting(
                          'lugs_on_tunnel_adjoining_base',
                          e.currentTarget.checked
                        )
                      }
                    />
                    {!!activeTunnel?.lugs_on_tunnel_adjoining_base && (
                      <div className="d-inline non-clickable">
                        <label>
                          <input
                            type="checkbox"
                            className="tunnel_settings_field hide"
                            name="show"
                            readOnly
                            checked={!!(activeTunnel as Record<string, unknown>).show}
                          />
                          <span className="icon-eye">show in model</span>
                        </label>
                      </div>
                    )}
                    <span className="label">
                      <span>Lugs on adjoining tunnel bases</span>
                    </span>
                  </div>
                </li>
                <li className={lockedClass(false)}>
                  <div className="">
                    <input
                      type="checkbox"
                      className="tunnel_settings_field"
                      name="lugs_on_split_line_base"
                      checked={!!activeTunnel?.lugs_on_split_line_base}
                      onChange={(e) =>
                        changeTunnelSetting('lugs_on_split_line_base', e.currentTarget.checked)
                      }
                    />
                    <span className="label">
                      <span>Lugs on split-line bases</span>
                    </span>
                  </div>
                </li>
                <li className="empty-row" />
                <li className={lockedClass(false)}>
                  <div className="lug-settings-row">
                    <div className="buttons-wrapper">
                      {activeLug ? (
                        <button
                          id="delete-lug"
                          className="icon-trash ka-has-tooltip"
                          {...{ tooltip: 'Delete Lug' }}
                          onClick={() => setDeletePopup(true)}
                        />
                      ) : (
                        <button
                          id="add-lug"
                          className="ka-has-tooltip"
                          {...{ tooltip: 'Add Lug' }}
                          onClick={addLug}
                        >
                          <span>+</span> Add
                        </button>
                      )}
                    </div>
                    <span className="label bold">
                      <span>Lug Settings</span>
                    </span>
                  </div>
                </li>
                <li className={`hint-row${lockedClass(false)}`}>
                  <div className="">
                    <span className="label">
                      <span>To select a lug, click on it in the 3D model</span>
                      <span>
                        Press and hold the CTRL key, in order to select two lugs
                        simultaneously.
                      </span>
                    </span>
                  </div>
                </li>
                {activeLug && (
                  <>
                    <li className={lockedClass(false)}>
                      <div className="single-line">
                        <select
                          className="tunnel_lugs_field"
                          name="model"
                          value={activeLug.model == null ? '' : String(activeLug.model)}
                          onChange={(e) =>
                            changeLugField(
                              'model',
                              e.currentTarget.value ? Number(e.currentTarget.value) : null
                            )
                          }
                        >
                          <option className="hide" value="">
                            {emptyOptionName}
                          </option>
                          {lugModels.map((m) => (
                            <option key={Number(m.id)} value={Number(m.id)}>
                              {String(m.model ?? '')}
                            </option>
                          ))}
                        </select>
                        <span className="label">
                          <span>Model</span>
                        </span>
                      </div>
                    </li>
                    {/* Always locked: Attachment is not a choice, it is whatever
                        the selected Model comes with. The select is an empty
                        stub carried over from legacy, there only so the row
                        lines up with the real selects around it. */}
                    <li className="non-clickable">
                      <div className="single-line">
                        <span className="selection">
                          <span>{String(activeLug.attachment_name ?? '')}</span>
                        </span>
                        <select disabled onChange={() => undefined} value="">
                          <option value="" />
                        </select>
                        <span className="label">
                          <span>Attachment</span>
                        </span>
                      </div>
                    </li>
                    <li className={lockedClass(false)}>
                      <div className="single-line">
                        <select
                          className="tunnel_lugs_field"
                          name="base_direction"
                          value={
                            activeLug.base_direction == null
                              ? ''
                              : String(activeLug.base_direction)
                          }
                          onChange={(e) =>
                            changeLugField(
                              'base_direction',
                              e.currentTarget.value ? Number(e.currentTarget.value) : null
                            )
                          }
                        >
                          <option className="hide" value="">
                            {emptyOptionName}
                          </option>
                          {lugBaseDirections.map((d) => (
                            <option key={Number(d.id)} value={Number(d.id)}>
                              {String(d.name ?? '')}
                            </option>
                          ))}
                        </select>
                        <span className="label">
                          <span>Base Direction</span>
                        </span>
                      </div>
                    </li>
                    <li className={lockedClass(false)}>
                      <div className="">
                        <span className="unit"> in</span>
                        <input
                          readOnly={activeLug.base_direction === 2}
                          disabled={activeLug.base_direction === 2}
                          data-type="number"
                          data-step="0.01"
                          className="tunnel_lugs_field"
                          name="x"
                          defaultValue={activeLug.x == null ? '' : String(activeLug.x)}
                          placeholder="varies..."
                          onBlur={(e) => {
                            const v = e.currentTarget.value;
                            if (v !== '') changeLugField('x', Number(v));
                          }}
                        />
                        <span className="label">
                          <span>X (tunnel length)</span>
                        </span>
                      </div>
                    </li>
                    <li className={lockedClass(false)}>
                      <div className="">
                        <span className="unit"> in</span>
                        <input
                          readOnly={activeLug.base_direction === 1}
                          disabled={activeLug.base_direction === 1}
                          data-type="number"
                          data-step="0.01"
                          className="tunnel_lugs_field"
                          name="y"
                          defaultValue={activeLug.y == null ? '' : String(activeLug.y)}
                          placeholder="varies..."
                          onBlur={(e) => {
                            const v = e.currentTarget.value;
                            if (v !== '') changeLugField('y', Number(v));
                          }}
                        />
                        <span className="label">
                          <span>Y (tunnel width)</span>
                        </span>
                      </div>
                    </li>
                    {showHint && (
                      <li>
                        <div className="brake-line">
                          <span className="label">
                            <span>
                              In order to view the lugs on the adjoining base(s) of a
                              tunnel, select a specific tunnel.
                            </span>
                          </span>
                        </div>
                      </li>
                    )}
                    <li className="empty-row" />
                    <li
                      className="toggle-cms-info always-visible"
                      onClick={() => setShowCmsInfo((v) => !v)}
                    >
                      <div className="">
                        <span className="label bold">
                          <span>Manufacturer&apos;s lug placement standards ...</span>
                        </span>
                      </div>
                    </li>
                    {showCmsInfo && (
                      <>
                        <li className="cms-info-item always-visible">
                          <div className="">
                            <span className="selection">
                              <span>{num2(activeLug.min_dist_from_base_edge)}</span>
                            </span>
                            <span className="label">Minimum Distance from base edge</span>
                            <span className="unit">in</span>
                          </div>
                        </li>
                        <li className="cms-info-item always-visible">
                          <div className="">
                            <span className="selection">
                              <span>{num2(activeLug.max_center_spacing)}</span>
                            </span>
                            <span className="label">Maximum lug center spacing</span>
                            <span className="unit">in</span>
                          </div>
                        </li>
                        <li className="cms-info-item always-visible">
                          <div className="">
                            <span className="selection">
                              <span>{String(activeLug.max_weight_per_lug ?? '')}</span>
                            </span>
                            <span className="label">Max weight per lug</span>
                            <span className="unit">lb</span>
                          </div>
                        </li>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </ul>
      {recalcButton}
      {defaultsPopup && (
        <UniversalPopup
          title={LUG_DEFAULTS_COPY[defaultsPopup].title}
          content={LUG_DEFAULTS_COPY[defaultsPopup].content}
          actionText={LUG_DEFAULTS_COPY[defaultsPopup].actionText}
          cancelText="CANCEL"
          onAction={() => confirmDefaults(defaultsPopup)}
          onCancel={() => setDefaultsPopup(null)}
        />
      )}
      {deletePopup && (
        <DeleteConfirmPopup
          title="Delete Lug?"
          content="If you choose to Delete, the lug and associated data will be deleted and will not be recoverable."
          onDelete={deleteActiveLugs}
          onCancel={() => setDeletePopup(false)}
        />
      )}
    </>
  );
};

export default LiftingLugPane;
